import { useMemo, useState, useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'

import { rem } from 'polished'
import styled from 'styled-components'
import qs from 'qs'

import { mediaQueryUp } from 'components/utils/breakpoint'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'

import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'

import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import * as Analytics from 'analytics/analytics'
import { trackCruisesClickEvent } from 'lib/cruises/cruiseUtils'

const PortsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr);
  }
`

function CruisePageBrowseByPorts() {
  const intl = useIntl()
  const [selectedTab, setSelectedTab] = useState<string>('destinations')

  const [destinationsFacets] = useCruiseSearchFacets({ facetTypes: ['destination_ports'] })
  const [departurePortsFacets] = useCruiseSearchFacets({ facetTypes: ['departure_ports'] })

  const [destinationsRef, destinationsInView] = useInView({
    triggerOnce: true,
    threshold: 1,
  })
  const [departurePortsRef, departurePortsInView] = useInView({
    triggerOnce: true,
    threshold: 1,
  })

  const handleTabClick = useCallback((tabName: string) => {
    trackCruisesClickEvent(`Browse by popular ports - ${tabName}`)
    setSelectedTab(tabName)
  }, [])

  const handlePortClick = useCallback((portName: string, portType: 'destination' | 'departure_port') => {
    trackCruisesClickEvent(`Browse by popular ports - ${portType} - ${portName}`)
  }, [])

  const handleViewMoreClick = useCallback(() => {
    trackCruisesClickEvent('Browse by popular ports - View More')
  }, [])

  useEffect(() => {
    if (destinationsInView && selectedTab === 'destinations') {
      Analytics.trackClientEvent({
        subject: 'Browse by popular ports - destinations',
        action: 'viewed',
        category: 'cruise',
        type: 'interaction',
      })
    }
    if (departurePortsInView && selectedTab === 'departurePorts') {
      Analytics.trackClientEvent({
        subject: 'Browse by popular ports - departurePorts',
        action: 'viewed',
        category: 'cruise',
        type: 'interaction',
      })
    }
  }, [destinationsInView, departurePortsInView, selectedTab])

  const mostPopularDestinations = useMemo(() => {
    return destinationsFacets
      // this filter is used to ignore high level destinations like "Europe", we need to update BE to return destination type
      .filter((facet) => facet.name.includes(','))
      .map((facet) => ({
        ...facet,
        name: facet.name.split(',')[0],
      }))
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
      .slice(0, 12)
  }, [destinationsFacets])

  const mostPopularDeparturePorts = useMemo(() => {
    return departurePortsFacets
      // this filter is used to ignore high level destinations like "Europe", we need to update BE to return destination type
      .filter((facet) => facet.name.includes(','))
      .map((facet) => ({
        ...facet,
        name: facet.name.split(',')[0],
      }))
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
      .slice(0, 12)
  }, [departurePortsFacets])

  const browseByPopularPortsHeading = intl.formatMessage({
    id: 'MQbTpjfkxXRIyna6Qh2w',
    defaultMessage: 'Browse by popular ports',
    description: 'Heading for the browse by popular ports section on cruise landing page',
  })

  const destinationsTabLabel = intl.formatMessage({
    id: '96JIVrB_o_Hl-RanS7Pq',
    defaultMessage: 'Destinations',
    description: 'Tab button label to show cruise destination ports in the cruises section',
  })

  const departurePortsTabLabel = intl.formatMessage({
    id: 'j69SRgTutK5YI549FUbw',
    defaultMessage: 'Departure ports',
    description: 'Tab button label to show cruise departure ports in the cruises section',
  })

  const viewMoreLabel = intl.formatMessage({
    id: 'Ii1wzim_mbo0ok6PuhaD',
    defaultMessage: 'View more',
    description: 'Button text to view more cruise port options in the cruise section',
  })

  return <Group gap={32} direction="vertical">
    <LayoutContainer>
      <Group direction="vertical" gap={16}>
        <Heading variant="heading3">
          {browseByPopularPortsHeading}
        </Heading>

        <TabButtonsGroup>
          <TabButton
            isActive={selectedTab === 'destinations'}
            onClick={() => handleTabClick('destinations')}
            size="large"
          >
            {destinationsTabLabel}
          </TabButton>

          <TabButton
            isActive={selectedTab === 'departurePorts'}
            onClick={() => handleTabClick('departurePorts')}
            size="large"
          >
            {departurePortsTabLabel}
          </TabButton>
        </TabButtonsGroup>
      </Group>
    </LayoutContainer>

    <LayoutContainer>
      {selectedTab === 'destinations' && (
        <PortsGrid ref={destinationsRef}>
          {mostPopularDestinations.map((facet) => (
            <Group key={facet.value} direction="vertical" gap={8} horizontalAlign="start">
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage(
                  {
                    id: 'iK4F5hzJhBfe_ii2gb3p',
                    defaultMessage: 'Cruises to {name}',
                    description: 'Label showing cruise destination port name in the cruise section',
                  },
                  { name: facet.name },
                )}
              </BodyText>
              <TextLink
                size="caption"
                underline
                colour="neutral-three"
                to={`/search/cruises?${qs.stringify({
                  destinationNames: [facet.name],
                  destinationIds: [facet.value],
                }, { arrayFormat: 'repeat' })}`}
                onClick={() => handlePortClick(facet.name, 'destination')}
              >
                {intl.formatMessage(
                  {
                    id: 'hSe_AMtZiQz72OEThctj',
                    defaultMessage: 'View {count, plural, one {# cruise} other {# cruises}}',
                    description: 'Link text showing number of cruises available to a destination',
                  },
                  { count: facet.count },
                )}
              </TextLink>
            </Group>
          ))}
        </PortsGrid>
      )}

      {selectedTab === 'departurePorts' && (
        <PortsGrid ref={departurePortsRef}>
          {mostPopularDeparturePorts.map((facet) => (
            <Group key={facet.value} direction="vertical" gap={8} horizontalAlign="start">
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage(
                  {
                    id: 'pnrwAMhV43WF2ZpRx3KX',
                    defaultMessage: 'Cruises from {name}',
                    description: 'Label showing cruise departure port name in the cruise section',
                  },
                  { name: facet.name },
                )}
              </BodyText>
              <TextLink
                size="caption"
                underline
                colour="neutral-three"
                to={`/search/cruises?${qs.stringify({
                  departurePortNames: [facet.name],
                  departurePortIds: [facet.value],
                }, { arrayFormat: 'repeat' })}`}
                onClick={() => handlePortClick(facet.name, 'departure_port')}
              >
                {intl.formatMessage(
                  {
                    id: 'X0FTaMrI8zxAq82neHsp',
                    defaultMessage: 'View {count, plural, one {# cruise} other {# cruises}}',
                    description: 'Link text showing number of cruises available from a departure port',
                  },
                  { count: facet.count },
                )}
              </TextLink>
            </Group>
          ))}
        </PortsGrid>
      )}
    </LayoutContainer>

    <Group direction="horizontal" horizontalAlign="center">
      <TextButton
        to="/search/cruises"
        kind="secondary"
        size="large"
        onClick={handleViewMoreClick}
      >
        {viewMoreLabel}
      </TextButton>
    </Group>
  </Group>
}

export default CruisePageBrowseByPorts
